import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Container,
  Card,
  CardBody,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Input,
  Spinner,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import attachmentStyles from "../css/attachmentfolder.module.scss";
import {
  formatDate,
  perPageData,
  showToastMessage,
} from "../../../../common/service/CommonService";
import tableStyles from "../../../../assets/scss/common/table.module.scss";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import Reason from "../../../../common/utilities/Reason/Reason";
import CommonTableIndexWise from "../../../../Components/Common/CommonTableIndexWise";
import FilePreviewModal from "../components/FilePreviewModal";

import { queryClient } from "../../../../api/queryClient";
import {
  PERMISSION_ACTIONS,
  PERMISSION_MODULES,
} from "../../../../common/constants/PermissionConstants";
import { usePermission } from "../../../../context/PermissionContext";
import ActionGuard from "../../../../Components/Common/ActionGuard";
import CommonSearch from "Components/Common/CommonSearch";
import { useAuditService } from "modules/Audit/api/useAuditQuery";
import CommonButton from "Components/Common/CommonButton";
import { useGlobalStudy } from "context/StudyContext";
import NoStudySelectedPlaceholder from "../../../../Components/Common/NoStudySelectedPlaceholder";
import { useFolderService } from "../api/useFolderService";
import { useFileService } from "../api/useFileService";

/* -----------------------------
 Types
----------------------------- */

interface StudyAttachmentsProps {
  studyId?: number | null;
}

interface Folder {
  id: number;
  name: string;
  lastModified: string;
  lastModifiedBy?: string;
  parentid: number | null;
  total_count: number;
}

interface CombinedItem {
  id: number;
  name: string;
  type: "folder" | "file";
  displayName: React.ReactNode;
  lastModified: string;
  lastModifiedBy: string;
  size?: string;
  fileUrl?: string;
  filepath?: string;
  total_count: number;
  parentid?: number | null;
}

/* -----------------------------
 Helpers
----------------------------- */

const getFileIcon = (fileName: string) => {
  const ext = fileName.split(".").pop()?.toLowerCase();
  switch (ext) {
    case "pdf":
      return { icon: "ri-file-pdf-line", color: "#e74c3c" };
    case "doc":
    case "docx":
      return { icon: "ri-file-word-line", color: "#2980b9" };
    case "xls":
    case "xlsx":
      return { icon: "ri-file-excel-line", color: "#27ae60" };
    case "png":
    case "jpg":
    case "jpeg":
      return { icon: "ri-image-line", color: "#8e44ad" };
    default:
      return { icon: "ri-file-line", color: "#7f8c8d" };
  }
};

const splitFileName = (fileName: string) => {
  const lastDot = fileName.lastIndexOf(".");
  if (lastDot === -1) return { baseName: fileName, extension: "" };
  return {
    baseName: fileName.slice(0, lastDot),
    extension: fileName.slice(lastDot),
  };
};

/* -----------------------------
 Component
----------------------------- */

const StudyAttachments: React.FC<StudyAttachmentsProps> = ({ studyId }) => {
  //-------------------------------------------------------------------------------------------
  //--> STATE
  //-------------------------------------------------------------------------------------------
  document.title = "Document Management | ZENTIXS VISTA";
  const [searchText, setSearchText] = useState<string>("");
  const [navigationStack, setNavigationStack] = useState<Folder[]>([]);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [tableKey, setTableKey] = useState<number>(0);
  const [fileObjects, setFileObjects] = useState<{ id: number; file: File }[]>(
    [],
  );
  const { selectedStudy:globalstudy } = useGlobalStudy();
  const [showDeleteReason, setShowDeleteReason] = useState<boolean>(false);
  const [activeStudyId, setActiveStudyId] = useState<number | null>(
    Number(globalstudy?.studyid)||studyId || null,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setActiveStudyId(Number(globalstudy?.studyid) || studyId || null);
    setNavigationStack([]);
  }, [globalstudy, studyId]);

  const currentFolder =
    navigationStack.length > 0
      ? navigationStack[navigationStack.length - 1]
      : null;

  const { hasPermission } = usePermission();
  const canview = hasPermission(
    PERMISSION_MODULES.DOCUMENT_MANAGEMENT,
    PERMISSION_ACTIONS.VIEW,
  );

  //-------------------------------------------------------------------------------------------
  //--> Data Fetching
  //-------------------------------------------------------------------------------------------

  // Fetch Folders and Files for current level
  const { InsertMutation, SelectAllQuery, DeleteMutation, UpdateMutation } =
    useFolderService(
      searchText,
      activeStudyId,
      currentPageNumber,
      perPageData,
      navigationStack.length === 0, // isEnabled (only at root)
    );

  const {
    FilesSelectAllQuery,
    ParentFilesSelectAllQuery,
    FileUploadMutation,
    FileDeleteMutation,
  } = useFileService(
    searchText,
    currentFolder?.id || null,
    currentPageNumber,
    perPageData,
    navigationStack.length === 1, // isParentSelectAll
  );
  const { SelectAllMutation: AuditMutation } = useAuditService();

  const isParentLevel = navigationStack.length === 1;
  const isRootLevel = navigationStack.length === 0;
  const { data: folderRes, isFetching: isFolderFetching } =
    SelectAllQuery as any;

  // Inside a main folder (stack length 1), we use ParentFilesSelectAll results
  // Otherwise (subfolders or root), we use standard FilesSelectAll
  const activeFileQuery = isParentLevel
    ? ParentFilesSelectAllQuery
    : FilesSelectAllQuery;
  const { data: fileRes, isFetching: isFileFetching } = activeFileQuery as any;

  // Handle extraction based on API response structure
  let foldersRaw: any[] = [];
  let filesRaw: any[] = [];
  let totalCount = 0;

  // 1. Reset variables to ensure zero leakage
  foldersRaw = [];
  filesRaw = [];
  totalCount = 0;

  // 2. Map queries to local variables with strict level checks
  const currentId = currentFolder ? Number(currentFolder.id) : null;

  if (navigationStack.length === 0) {
    // --- LEVEL 0: ROOT ---
    if (folderRes && folderRes.data) {
      foldersRaw = Array.isArray(folderRes.data)
        ? folderRes.data
        : (folderRes.data.data ?? []);
      totalCount =
        foldersRaw.length > 0
          ? foldersRaw[0]?.totalcount || foldersRaw[0]?.total_count || 0
          : 0;
    }
  } else if (navigationStack.length === 1 && currentId) {
    // --- LEVEL 1: MAIN FOLDER CONTENT ---
    // We use ParentFilesSelectAllQuery here.
    if (fileRes && fileRes.data) {
      const folderSrc = fileRes.data.folders;
      const folderArr = Array.isArray(folderSrc)
        ? folderSrc
        : (folderSrc?.data ?? []);

      // STRICT FILTERING: Only include folders where parentid matches current Level 0 folder
      foldersRaw = folderArr.filter(
        (f: any) =>
          f &&
          f.folderid && // must have valid id
          f.foldername && // must have name
          Number(f.parentid) === currentId,
      );

      const fileSrc = fileRes.data.files;
      filesRaw = Array.isArray(fileSrc) ? fileSrc : (fileSrc?.data ?? []);

      const folderTot =
        folderSrc?.totalcount ||
        folderSrc?.total_count ||
        foldersRaw[0]?.totalcount ||
        foldersRaw[0]?.total_count ||
        0;
      const fileTot =
        fileSrc?.totalcount ||
        fileSrc?.total_count ||
        (Array.isArray(filesRaw) && filesRaw.length > 0
          ? filesRaw[0]?.totalcount || filesRaw[0]?.total_count
          : 0);
      totalCount = Number(folderTot || 0) + Number(fileTot || 0);
    }
  } else if (currentId) {
    // --- LEVEL 2+: DEEPER LEVELS ---
    // At deeper levels, we use standard FilesSelectAllQuery
    if (fileRes && fileRes.data) {
      const payload = Array.isArray(fileRes.data)
        ? fileRes.data
        : (fileRes.data.data ?? []);
      filesRaw = payload.filter(
        (f: any) =>
          f &&
          (f.folderid || f.parentid) &&
          Number(f.folderid || f.parentid) === currentId,
      );
      totalCount =
        filesRaw.length > 0
          ? filesRaw[0]?.totalcount || filesRaw[0]?.total_count || 0
          : 0;
    }
  }
  // Map Folders
  const displaylink = (item: any) => {
    if (canview) {
      return (
        <span
          style={{ cursor: "pointer" }}
          className="text-primary fw-medium"
          onClick={() => handleFolderSelection(item)}
        >
          <i className="ri-folder-fill me-2" style={{ color: "#f39c12" }}></i>
          {item.foldername}
        </span>
      );
    } else {
      return (
        <span style={{ cursor: "pointer" }} className="fw-medium">
          <i className="ri-folder-fill me-2" style={{ color: "#f39c12" }}></i>
          {item.foldername}
        </span>
      );
    }
  };
  const folders: CombinedItem[] = foldersRaw.map((item: any) => ({
    id: item.folderid || item.id,
    name: item.foldername,
    type: "folder",
    parentid: item.parentid ?? null,
    lastModifiedBy: item.updatebyname || item.createdbyname || "System",
    lastModified:
      (item.updateddate
        ? formatDate(item.updateddate)
        : formatDate(item.createddate)) ?? "",
    total_count: item.total_count || item.totalcount || 0,
    displayName: displaylink(item),
    // displayName: (
    //     <span
    //         style={{ cursor: "pointer" }}
    //         className="text-primary fw-medium"
    //         onClick={() => handleFolderSelection(item)}
    //     >
    //         <i className="ri-folder-fill me-2" style={{ color: "#f39c12" }}></i>
    //         {item.foldername}
    //     </span>
    // )
  }));

  // Map Files
  const files: CombinedItem[] = filesRaw.map((item: any) => {
    const name = item.filename || item.name || "Unnamed File";
    const { icon, color } = getFileIcon(name);
    return {
      id: item.fileid || item.id,
      name: name,
      type: "file",
      lastModifiedBy: item.updatebyname || item.createdbyname || "System",
      lastModified:
        (item.updateddate
          ? formatDate(item.updateddate)
          : formatDate(item.createddate)) ?? "",
      total_count: item.totalcount || item.total_count || 0,
      filepath: item.path || "",
      displayName: (
        <span>
          <i className={`${icon} me-2`} style={{ color }}></i>
          {name}
        </span>
      ),
    };
  });

  const combinedData = [...folders, ...files];
  //-------------------------------------------------------------------------------------------
  //--> Navigation logic
  //-------------------------------------------------------------------------------------------

  const handleFolderSelection = (item: any) => {
    const folder: Folder = {
      id: item.folderid || item.id,
      name: item.foldername || item.name,
      lastModified: "",
      parentid: item.parentid ?? currentFolder?.id ?? null,
      total_count: 0,
    };

    if (navigationStack.length === 0) {
      setActiveStudyId(item.studyid); // <-- IMPORTANT
    }

    setNavigationStack([...navigationStack, folder]);
    setSearchText("");
    setCurrentPageNumber(1);
  };

  const handleBreadcrumbClick = (index: number) => {
    if (index === -1) {
      setNavigationStack([]);
    } else {
      setNavigationStack(navigationStack.slice(0, index + 1));
    }
    setSearchText("");
    setCurrentPageNumber(1);
  };

  const onSearch = (text: string | null) => {
    setSearchText(text ?? "");
    setCurrentPageNumber(1);
  };

  //-------------------------------------------------------------------------------------------
  //--> Modals & Actions
  //-------------------------------------------------------------------------------------------

  const [showFolderModal, setShowFolderModal] = useState(false);
  const [folderName, setFolderName] = useState("");

  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState<any>(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [previewFileUrl, setPreviewFileUrl] = useState<string | null>(null);

  const [renameModal, setRenameModal] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [renameItem, setRenameItem] = useState<any>(null);
  const [isReasonModal, setIsReasonModal] = useState(false);
  const [fileExtension, setFileExtension] = useState("");
  const [isAuditPreview, setIsAuditPreview] = useState<boolean>(false);

  const toggleDeleteModal = (): void => {
    setDeleteModal(!deleteModal);
    setShowDeleteReason(false);
  };
  const toggleDeleteReason = (): void => setShowDeleteReason(!showDeleteReason);

  const handleCreateFolder = () => {
    if (!folderName.trim()) return;

    InsertMutation.mutate(
      {
        foldername: folderName.trim(),
        studyid: activeStudyId as number,
        parentid: currentFolder?.id || null,
      },
      {
        onSuccess: (res) => {
          queryClient.invalidateQueries({ queryKey: ["folder-selectAll"] });
          queryClient.invalidateQueries({
            queryKey: ["folder-parentSelectAll"],
          });
          showToastMessage("success", res?.message);
          setShowFolderModal(false);
          setFolderName("");
        },
        onError: (err) => showToastMessage("error", err?.message ?? err),
      },
    );
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentFolder) return;

    // Check file size (10 MB limit)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      showToastMessage("error", "File size should not be larger than 10MB");
      e.target.value = "";
      return;
    }

    const formData = new FormData();
    formData.append("folderid", currentFolder.id.toString());
    formData.append("file", file);
    FileUploadMutation.mutate(formData, {
      onSuccess: (res) => {
        queryClient.invalidateQueries({ queryKey: ["file-selectAll"] });
        queryClient.invalidateQueries({ queryKey: ["folder-parentSelectAll"] });
        showToastMessage("success", res?.message);
      },
      onError: (err) => showToastMessage("error", err?.message ?? err),
    });
    e.target.value = "";
  };

  const onEditItem = (item: any) => {
    setRenameItem(item);
    if (item.type === "file") {
      const { baseName, extension } = splitFileName(item.name);
      setRenameValue(baseName);
      setFileExtension(extension);
    } else {
      setRenameValue(item.name);
      setFileExtension("");
    }
    setRenameModal(true);
  };

  const btnReasonClick = (reasonPayload: { reason: string }) => {
    if (!renameItem) return;

    const payload = {
      folderid: renameItem.id,
      studyid: activeStudyId as number,
      foldername: renameValue.trim(),
      updatereason: reasonPayload.reason,
      parentid: currentFolder?.id || null,
    };

    UpdateMutation.mutate(payload, {
      onSuccess: (res) => {
        queryClient.invalidateQueries({ queryKey: ["folder-selectAll"] });
        queryClient.invalidateQueries({ queryKey: ["folder-parentSelectAll"] });
        queryClient.invalidateQueries({ queryKey: ["file-selectAll"] });
        setIsReasonModal(false);
        setRenameModal(false);
        setTableKey((prev) => prev + 1); // Force re-render of table
        setCurrentPageNumber(1);
        showToastMessage("success", res?.message);
      },
      onError: (err: any) => showToastMessage("error", err?.message ?? err),
    });
  };

  const confirmDelete = (reasonPayload: { reason: string }) => {
    if (!selectedDeleteItem) return;

    const mutation =
      selectedDeleteItem.type === "folder"
        ? DeleteMutation
        : FileDeleteMutation;
    const payload =
      selectedDeleteItem.type === "folder"
        ? { id: selectedDeleteItem.id, updatereason: reasonPayload.reason }
        : { fileid: selectedDeleteItem.id, updatereason: reasonPayload.reason };

    mutation.mutate(payload, {
      onSuccess: (res) => {
        queryClient.invalidateQueries({
          queryKey: [
            selectedDeleteItem.type === "folder"
              ? "folder-selectAll"
              : "file-selectAll",
          ],
        });
        queryClient.invalidateQueries({ queryKey: ["folder-parentSelectAll"] });
        toggleDeleteModal();
        setCurrentPageNumber(1);
        showToastMessage("success", res?.message);
      },
      onError: (err) => showToastMessage("error", err?.message ?? err),
    });
  };

  const API_FILE_BASE_URL =
    sessionStorage.getItem("filebaseUrl") ||
    process.env.REACT_APP_FILE_BASE_URL;

  const handleViewFile = (item: any) => {
    const baseUrl = API_FILE_BASE_URL || "";
    const url = item.filepath || "";

    const finalUrl = url.startsWith("http")
      ? url
      : `${baseUrl}${url.startsWith("/") ? url : "/" + url}`;

    setPreviewFile(null);
    setPreviewFileUrl(finalUrl);
    setIsAuditPreview(false);
    setPreviewOpen(true);
  };

  const handleDownloadFile = (item: any) => {
    if (!item.filepath) return;
  const baseUrl = API_FILE_BASE_URL || "";
  const url = item.filepath || "";

  const finalUrl = url.startsWith("http")
    ? url
    : `${baseUrl}${url.startsWith("/") ? url : "/" + url}`;

  const link = document.createElement("a");
  link.href = finalUrl;
  link.download = item.name || "download";
  link.target = "_blank";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

  const onAuditClick = () => {
    const payload: any = {
      modulename: "Study Document",
      studyid: activeStudyId,
    };

    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data instanceof Blob) {
          const pdfFile = new File([data], "Study_Document_Audit_Log.pdf", {
            type: "application/pdf",
          });
          setPreviewFile(pdfFile);
          setPreviewFileUrl(null);
          setIsAuditPreview(true);
          setPreviewOpen(true);
        }
      },
      onError: (error: any) => {
        showToastMessage("error", "Error fetching audit logs");
        console.error("Error fetching audit logs:", error);
      },
    });
  };

  const getRowActions = (item: any): any => {
    if (!currentFolder) return {};

    const rowActions: any = {};

    if (item.type === "folder") {
      rowActions.onRename = (r: any) => onEditItem(r);
      rowActions.onDelete = (r: any) => {
        setSelectedDeleteItem(r);
        toggleDeleteModal();
      };
    } else if (item.type === "file") {
      rowActions.onView = (r: any) => handleViewFile(r);
      rowActions.onDownload = (r: any) => handleDownloadFile(r);
      rowActions.onDelete = (r: any) => {
        setSelectedDeleteItem(r);
        toggleDeleteModal();
      };
    }

    return rowActions;
  };

  return (
    <div className="page-content">
      <Container fluid className="px-0">
        {!globalstudy?.studyid ? (
          <div >
            <NoStudySelectedPlaceholder moduleName="document management" />
          </div>
        ) : (
          <Card className={tableStyles.card}>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center gap-3">
                  <CommonSearch onSearch={onSearch} searchText={searchText} />
                  <div className={attachmentStyles.breadcrumbContainer}>
                    <div
                      className={`${attachmentStyles.breadcrumbItem} ${navigationStack.length === 0 ? attachmentStyles.activeItem : ""}`}
                      onClick={() => handleBreadcrumbClick(-1)}
                    >
                      <i
                        className={
                          navigationStack.length === 0
                            ? "ri-folder-open-line"
                            : "ri-folder-fill"
                        }
                      ></i>
                      <span>Folders</span>
                    </div>

                    {navigationStack.map((folder, idx) => (
                      <React.Fragment key={folder.id}>
                        <div className={attachmentStyles.separator}>
                          <i className="ri-arrow-right-s-line"></i>
                        </div>
                        <div
                          className={`${attachmentStyles.breadcrumbItem} ${idx === navigationStack.length - 1 ? attachmentStyles.activeItem : ""}`}
                          onClick={() => handleBreadcrumbClick(idx)}
                        >
                          <i
                            className={
                              idx === navigationStack.length - 1
                                ? "ri-folder-open-line"
                               : "ri-folder-fill"
                            }
                          ></i>
                          <span>{folder.name}</span>
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                {/* Actions Dropdown Menu */}
                <div className="d-flex gap-2">
                  {(navigationStack.length === 1 || currentFolder) && (
                    <ActionGuard
                      module={PERMISSION_MODULES.DOCUMENT_MANAGEMENT}
                      action={PERMISSION_ACTIONS.ADD}
                    >
                      <UncontrolledDropdown direction="down">
                        <DropdownToggle
                          tag="button"
                          className="btn btn-primary btn-sm d-flex align-items-center"
                        >
                          + Create <i className="ri-arrow-down-s-line ms-1"></i>
                        </DropdownToggle>
                        <DropdownMenu
                          className="dropdown-menu-end shadow-sm border-light"
                          style={{ minWidth: "140px", padding: "4px 0" }}
                        >
                          {/* Create Folder: Root or Level 1 (Parent) */}
                          {navigationStack.length === 1 && (
                            <DropdownItem
                              onClick={() => {
                                setFolderName("");
                                setShowFolderModal(true);
                              }}
                              style={{ fontSize: "12px", padding: "6px 16px" }}
                              className="d-flex align-items-center"
                            >
                              <div
                                className="bg-warning-subtle text-warning rounded d-flex align-items-center justify-content-center me-2"
                                style={{ width: "22px", height: "22px" }}
                              >
                                <i className="ri-folder-add-line"></i>
                              </div>
                              Create Folder
                            </DropdownItem>
                          )}

                          {/* Upload File: Any level except Root */}
                          {currentFolder && (
                            <DropdownItem
                              tag="button"
                              onClick={() => fileInputRef.current?.click()}
                              className="mb-0 d-flex align-items-center w-100"
                              style={{
                                cursor: "pointer",
                                fontSize: "12px",
                                padding: "6px 16px",
                              }}
                            >
                              <div
                                className="bg-info-subtle text-info rounded d-flex align-items-center justify-content-center me-2"
                                style={{ width: "22px", height: "22px" }}
                              >
                                <i className="ri-file-upload-line"></i>
                              </div>
                              Upload File
                            </DropdownItem>
                          )}
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </ActionGuard>
                  )}

                  {(navigationStack.length === 1) && (
                    <CommonButton
                      action={PERMISSION_ACTIONS.AUDIT}
                      module={PERMISSION_MODULES.DOCUMENT_MANAGEMENT}
                      color="primary"
                      size="sm"
                      onClick={onAuditClick}
                      disabled={AuditMutation.isPending}
                      outline
                    >
                      {AuditMutation.isPending ? (
                        <Spinner size="sm" className="me-2" />
                      ) : (
                        <i className="ri-history-line me-1"></i>
                      )}
                      Audit
                    </CommonButton>
                  )}

                  {/* Hidden input placed outside the dropdown so it doesn't get unmounted when the dropdown closes */}
                  {currentFolder && (
                    <Input
                      innerRef={fileInputRef}
                      type="file"
                      hidden
                      onChange={handleFileUpload}
                    />
                  )}
                </div>
              </div>

              <CommonTableIndexWise
                moduleName={PERMISSION_MODULES.DOCUMENT_MANAGEMENT}
                key={`${tableKey}-${navigationStack.length}-${currentPageNumber}`}
                columns={[
                  { key: "displayName", label: "Name" },
                  { key: "lastModifiedBy", label: "Last Modified By" },
                  { key: "lastModified", label: "Last Modified Date" },
                ]}
                data={combinedData}
                showStatus={false}
                actions={getRowActions}
                pageSize={perPageData}
                currentPage={currentPageNumber}
                onPageChange={setCurrentPageNumber}
                totalRecords={totalCount}
                loading={isFolderFetching || isFileFetching}
              />
            </CardBody>
          </Card>
        )}

        {/* Create Folder Modal */}
        <Modal
          isOpen={showFolderModal}
          toggle={() => setShowFolderModal(false)}
          centered
          size="sm"
        >
          <ModalHeader toggle={() => setShowFolderModal(false)}>
            <i className="ri-folder-add-line me-2 text-warning"></i>
            Create {currentFolder ? "Subfolder" : "Folder"}
          </ModalHeader>
          <ModalBody>
            <Label>Folder Name</Label>
            <Input
              type="text"
              placeholder="Enter name"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreateFolder()}
              autoFocus
            />
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              size="sm"
              outline
              onClick={() => setShowFolderModal(false)}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              size="sm"
              onClick={handleCreateFolder}
              disabled={!folderName.trim() || InsertMutation.isPending}
            >
              {InsertMutation.isPending ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  <span>create</span>
                </>
              ) : (
                "Create"
              )}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Rename Modal */}
        <Modal
          isOpen={renameModal}
          toggle={() => {
            setRenameModal(false);
            setFileExtension("");
          }}
          centered
          size="sm"
        >
          <ModalHeader
            toggle={() => {
              setRenameModal(false);
              setFileExtension("");
            }}
          >
            <i className="ri-pencil-line me-2 text-primary"></i>
            Rename {renameItem?.type === "folder" ? "Folder" : "File"}
          </ModalHeader>
          <ModalBody>
            <Label>New Name</Label>
            <div className="d-flex align-items-center">
              <Input
                type="text"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" &&
                  (renameItem.type === "folder"
                    ? setIsReasonModal(true)
                    : setRenameModal(false))
                }
                autoFocus
              />
              {fileExtension && (
                <span className="ms-1 text-muted">{fileExtension}</span>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              size="sm"
              outline
              onClick={() => {
                setRenameModal(false);
                setFileExtension("");
              }}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              size="sm"
              onClick={() => {
                if (renameItem.type === "folder") setIsReasonModal(true);
                else setRenameModal(false); // Placeholder for file rename logic
              }}
            >
              Rename
            </Button>
          </ModalFooter>
        </Modal>

        {isReasonModal && (
          <Reason
            isOpen={isReasonModal}
            toggle={() => setIsReasonModal(false)}
            action="Update"
            id={renameItem?.id}
            click={btnReasonClick}
            isLoading={UpdateMutation.isPending}
          />
        )}

        {deleteModal && (
          <DeleteModal
            isopen={deleteModal}
            toggle={toggleDeleteModal}
            recordId={selectedDeleteItem?.id}
            recordName={selectedDeleteItem?.name}
            onDeleteClick={confirmDelete}
            showDeleteReason={showDeleteReason}
            toggleDeleteReason={toggleDeleteReason}
            isLoading={
              selectedDeleteItem?.type === "folder"
                ? DeleteMutation.isPending
                : FileDeleteMutation.isPending
            }
          />
        )}

        <FilePreviewModal
          isOpen={previewOpen}
          file={previewFile}
          fileUrl={previewFileUrl}
          size={isAuditPreview ? "xl" : "lg"}
          onClose={() => {
            setPreviewOpen(false);
            setPreviewFileUrl(null);
            setIsAuditPreview(false);
          }}
        />
      </Container>
    </div>
  );
};

export default StudyAttachments;
